import { useQueryClient } from '@tanstack/react-query';

import { Campaign } from 'domains/campaigns/types';

import useArchiveCampaign from './mutations/useArchiveCampaign';
import QueryKeys from './queries/keys';

type Props = {
    activeItem: Campaign;
    refetchTableData: () => void;
    resetActiveItem: () => void;
    setErrorMessage: (error: string) => void;
    setSuccessMessage: (message: string) => void;
};

const useArchiveCampaignItem = ({
    activeItem,
    refetchTableData,
    resetActiveItem,
    setErrorMessage,
    setSuccessMessage,
}: Props) => {
    const queryClient = useQueryClient();

    const onError = (error: Error) => {
        setErrorMessage(error.message);
        resetActiveItem();
    };

    const onSuccess = () => {
        setSuccessMessage(`"${activeItem?.name}" successfully archived.`);
        refetchTableData();
        resetActiveItem();

        queryClient.invalidateQueries(QueryKeys.CAMPAIGNS_BASE);
        queryClient.invalidateQueries(QueryKeys.ARCHIVED_CAMPAIGNS);
        queryClient.invalidateQueries(['campaigns']);
    };

    const { isLoading, mutate: archiveCampaign, isSuccess } = useArchiveCampaign({
        onError,
        onSuccess,
    });

    const getArchiveItemFunction = () => {
        if (!activeItem) return () => {};
        return () => archiveCampaign({ id: activeItem.id });
    };

    return { getArchiveItemFunction, isLoading, isSuccess };
};

export default useArchiveCampaignItem;
